"use client";

import { useEffect, useId, useState } from "react";
import Image from "next/image";
import { Menu, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { Link, usePathname } from "@/i18n/navigation";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { track } from "@/components/analytics/track";

const LINKS = [
  {
    href: "/research",
    key: "research",
    match: (pathname: string) =>
      pathname === "/research" || pathname.startsWith("/research/"),
  },
  {
    href: "/start",
    key: "guide",
    match: (pathname: string) => pathname === "/start",
  },
  {
    href: "/product",
    key: "product",
    match: (pathname: string) => pathname === "/product",
  },
  {
    href: "/pricing",
    key: "pricing",
    match: (pathname: string) => pathname === "/pricing",
  },
] as const;

function linkClass(active: boolean) {
  return active
    ? "text-sm font-medium tracking-wide text-teal-light"
    : "text-sm font-medium tracking-wide text-slate-400 transition-colors hover:text-slate-100";
}

export function Header() {
  const t = useTranslations("Nav");
  const tc = useTranslations("Common");
  const pathname = usePathname();
  const onProductPage = pathname === "/product";
  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const ctaHref = onProductPage ? "/subscribe" : "/product";
  const ctaLabel = onProductPage ? t("subscribeToActivate") : t("download");

  return (
    <header className="fixed inset-x-0 top-[var(--announcement-bar-height,0px)] z-50 w-full border-b border-slate-800/50 bg-slate-900/90 backdrop-blur-md">
      <nav className="mx-auto flex w-full max-w-6xl items-center justify-between gap-2 px-4 py-3 sm:gap-4 sm:px-6">
        <Link href="/" className="flex min-w-0 items-center gap-2 sm:gap-3">
          <Image
            src="/atabet_logo.jpg"
            alt={tc("logoAlt")}
            width={48}
            height={48}
            className="h-10 w-10 shrink-0 rounded-md object-contain sm:h-12 sm:w-12"
            priority
          />
          <span className="truncate text-lg font-bold tracking-tight text-slate-100 uppercase">
            {tc("brandName")}
            <span className="hidden text-teal-light sm:inline">
              {tc("brandSuffix")}
            </span>
          </span>
        </Link>
        <div className="flex shrink-0 items-center gap-2 sm:gap-5">
          <div className="hidden items-center gap-6 md:flex">
            {LINKS.map((item) => {
              const active = item.match(pathname);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={linkClass(active)}
                  aria-current={active ? "page" : undefined}
                >
                  {t(item.key)}
                </Link>
              );
            })}
          </div>
          <LanguageSwitcher />
          <Link
            href={ctaHref}
            onClick={
              onProductPage
                ? undefined
                : () =>
                    track("cta_click", { meta: { label: "header_download" } })
            }
            className="hidden rounded-lg bg-maroon px-3 py-2 text-sm font-semibold whitespace-nowrap text-white transition-colors hover:bg-maroon-hover sm:inline-flex sm:px-4"
          >
            {ctaLabel}
          </Link>
          <button
            type="button"
            className="inline-flex rounded-md p-2 text-slate-300 transition-colors hover:bg-slate-800 hover:text-slate-100 md:hidden"
            aria-expanded={menuOpen}
            aria-controls={menuId}
            aria-label={menuOpen ? t("closeMenu") : t("openMenu")}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>
      {menuOpen ? (
        <div
          id={menuId}
          className="border-t border-slate-800/80 bg-slate-900/95 md:hidden"
        >
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-1 px-4 py-3 sm:px-6">
            {LINKS.map((item) => {
              const active = item.match(pathname);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`rounded-md px-2 py-2 ${linkClass(active)}`}
                  aria-current={active ? "page" : undefined}
                >
                  {t(item.key)}
                </Link>
              );
            })}
            <Link
              href={ctaHref}
              onClick={
                onProductPage
                  ? undefined
                  : () =>
                      track("cta_click", {
                        meta: { label: "header_download_mobile" },
                      })
              }
              className="mt-2 rounded-lg bg-maroon px-3 py-2 text-center text-sm font-semibold text-white transition-colors hover:bg-maroon-hover sm:hidden"
            >
              {ctaLabel}
            </Link>
          </div>
        </div>
      ) : null}
    </header>
  );
}
